/**
 * @file Modal.jsx
 * @description Accessible modal dialog with focus trap, scroll lock,
 * animated entrance/exit, and stacking support.
 *
 * @param {boolean} open - Whether the modal is visible
 * @param {Function} onClose - Called when modal should close
 * @param {'sm'|'md'|'lg'|'fullscreen'} [size='md']
 * @param {string} [title] - Header title text
 * @param {boolean} [closeOnOverlay=true] - Close when clicking overlay
 * @param {boolean} [closeOnEscape=true] - Close on Escape key
 * @param {boolean} [showCloseButton=true]
 * @param {React.ReactNode} [footer] - Footer content (usually buttons)
 * @param {string} [className]
 * @param {React.ReactNode} children - Modal body content
 */
import React, { useEffect, useRef, useCallback, useState } from 'react';

const sizeMap = {
  sm: '400px',
  md: '560px',
  lg: '720px',
  fullscreen: '100%',
};

/* Inject keyframes once */
let modalAnimInjected = false;
function injectModalAnimations() {
  if (modalAnimInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = `
@keyframes ib-modal-overlay-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes ib-modal-overlay-out {
  from { opacity: 1; }
  to { opacity: 0; }
}
@keyframes ib-modal-content-in {
  from { opacity: 0; transform: scale(0.95) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes ib-modal-content-out {
  from { opacity: 1; transform: scale(1) translateY(0); }
  to { opacity: 0; transform: scale(0.95) translateY(10px); }
}
@keyframes ib-modal-fullscreen-in {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}`;
  document.head.appendChild(style);
  modalAnimInjected = true;
}

/** Track stacked modals for z-index and scroll lock */
let modalStack = 0;

/** X icon */
function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Get all focusable elements within a container.
 */
function getFocusableElements(container) {
  const selectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll(selectors)).filter(
    (el) => !el.disabled && !el.hasAttribute('aria-hidden')
  );
}

function Modal({
  open,
  onClose,
  size = 'md',
  title,
  closeOnOverlay = true,
  closeOnEscape = true,
  showCloseButton = true,
  footer,
  className = '',
  children,
  style: userStyle,
  ...rest
}) {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const previousFocus = useRef(null);
  const [exiting, setExiting] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { injectModalAnimations(); }, []);

  // Mount/unmount with exit animation
  useEffect(() => {
    if (open) {
      setMounted(true);
      setExiting(false);
    } else if (mounted) {
      setExiting(true);
      const timer = setTimeout(() => {
        setMounted(false);
        setExiting(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll lock and focus management
  useEffect(() => {
    if (!mounted || exiting) return;

    modalStack++;
    previousFocus.current = document.activeElement;
    document.body.style.overflow = 'hidden';

    // Focus the first focusable element or the content itself
    requestAnimationFrame(() => {
      if (contentRef.current) {
        const focusable = getFocusableElements(contentRef.current);
        if (focusable.length > 0) {
          focusable[0].focus();
        } else {
          contentRef.current.focus();
        }
      }
    });

    return () => {
      modalStack--;
      if (modalStack === 0) {
        document.body.style.overflow = '';
      }
      // Restore focus
      if (previousFocus.current && typeof previousFocus.current.focus === 'function') {
        previousFocus.current.focus();
      }
    };
  }, [mounted, exiting]);

  // Escape key
  useEffect(() => {
    if (!mounted || !closeOnEscape) return;
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mounted, closeOnEscape, onClose]);

  // Focus trap
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Tab') return;
    if (!contentRef.current) return;

    const focusable = getFocusableElements(contentRef.current);
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  const handleOverlayClick = useCallback((e) => {
    if (closeOnOverlay && e.target === overlayRef.current) {
      onClose();
    }
  }, [closeOnOverlay, onClose]);

  if (!mounted) return null;

  const isFullscreen = size === 'fullscreen';
  const animDuration = '0.2s';
  const zIndex = 1200 + (modalStack - 1) * 10;

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'var(--overlay-medium)',
    display: 'flex',
    alignItems: isFullscreen ? 'stretch' : 'center',
    justifyContent: 'center',
    padding: isFullscreen ? 0 : 'var(--space-4)',
    zIndex,
    animation: `${exiting ? 'ib-modal-overlay-out' : 'ib-modal-overlay-in'} ${animDuration} var(--ease-out) forwards`,
  };

  const contentStyle = {
    position: 'relative',
    width: '100%',
    maxWidth: sizeMap[size],
    maxHeight: isFullscreen ? '100%' : '90vh',
    backgroundColor: 'var(--surface-raised)',
    borderRadius: isFullscreen ? 0 : 'var(--radius-xl)',
    boxShadow: isFullscreen ? 'none' : 'var(--shadow-xl)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    outline: 'none',
    animation: `${exiting ? 'ib-modal-content-out' : (isFullscreen ? 'ib-modal-fullscreen-in' : 'ib-modal-content-in')} ${animDuration} var(--ease-out) forwards`,
    ...userStyle,
  };

  return (
    <div
      ref={overlayRef}
      className={`ib-modal-overlay ${className}`}
      style={overlayStyle}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'ib-modal-title' : undefined}
        style={contentStyle}
        tabIndex={-1}
        {...rest}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4) var(--space-5)',
            borderBottom: '1px solid var(--border-default)',
            flexShrink: 0,
          }}>
            {title && (
              <h2
                id="ib-modal-title"
                style={{
                  fontSize: 'var(--text-h4)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--text-primary)',
                  margin: 0,
                  lineHeight: 'var(--leading-tight)',
                }}
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close dialog"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: 'var(--text-tertiary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginLeft: 'var(--space-2)',
                  transition: 'background var(--transition-fast)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--overlay-light)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: 'var(--space-5)',
          WebkitOverflowScrolling: 'touch',
        }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 'var(--space-3)',
            padding: 'var(--space-4) var(--space-5)',
            borderTop: '1px solid var(--border-default)',
            flexShrink: 0,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
